import React, { useState, useEffect, useRef, useCallback } from "react";
import { Plus, Minus, ArrowLeft, ShoppingCart, X } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { supabase } from "../../../lib/supabaseClient";
import toast from "react-hot-toast";
import { debounce } from "lodash";

interface MenuItem {
  id: string;
  name: string;
  description: string;
  price: number;
  image: string;
  category: "veg" | "non-veg";
}

interface SelectedItem {
  item: MenuItem;
  quantity: number;
}

interface FormData {
  name: string;
  contact: string;
  pickupTime: string;
  instructions: string;
  address: string;
}

// Helper function to get the ordinal suffix for a number
const getOrdinalSuffix = (n: number): string => {
  const s = ["th", "st", "nd", "rd"];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
};

const TakeawayMenu = () => {
  const [items, setItems] = useState<MenuItem[]>([]);
  const [activeCategory, setActiveCategory] = useState<"all" | "veg" | "non-veg">("all");
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortOption, setSortOption] = useState("");
  const [isButtonStatic, setIsButtonStatic] = useState(false);
  const [isBelowFooter, setIsBelowFooter] = useState(false);
  const [isCartModalOpen, setIsCartModalOpen] = useState(false);
  const { state } = useLocation();
  const navigate = useNavigate();
  const menuContainerRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLDivElement>(null);

  const formData = (state as { formData?: FormData; selectedItems?: SelectedItem[] })?.formData || {
    name: "",
    contact: "",
    pickupTime: "",
    instructions: "",
    address: "",
  };
  const initialSelectedItems = (state as { selectedItems?: SelectedItem[] })?.selectedItems || [];
  const [selectedItems, setSelectedItems] = useState<SelectedItem[]>(initialSelectedItems);

  useEffect(() => {
    const fetchMenuItems = async () => {
      try {
        setLoading(true);
        const { data, error } = await supabase
          .from("menu_items")
          .select("id, name, description, price, image_url, category")
          .order("name");

        if (error) throw error;

        if (data) {
          const formattedItems: MenuItem[] = data.map((item) => ({
            id: item.id,
            name: item.name,
            description: item.description || "",
            price: Number(item.price),
            image: item.image_url || "",
            category: item.category as "veg" | "non-veg",
          }));
          setItems(formattedItems);

          if (initialSelectedItems.length > 0) {
            const updatedSelectedItems = initialSelectedItems.map((si) => {
              const fullItem = formattedItems.find((i) => i.id === si.item.id) || si.item;
              return { item: fullItem, quantity: si.quantity };
            });
            setSelectedItems(updatedSelectedItems);
          }
        }
      } catch (error) {
        console.error("Error fetching menu items:", error);
        toast.error("Failed to load menu items.");
      } finally {
        setLoading(false);
      }
    };

    fetchMenuItems();
  }, []);

  const debouncedSearch = useCallback(debounce((value: string) => setSearchQuery(value), 300), []);

  useEffect(() => {
    const handleScroll = () => {
      if (!menuContainerRef.current || !buttonRef.current) return;

      const menuBottom = menuContainerRef.current.getBoundingClientRect().bottom;
      const buttonHeight = buttonRef.current.getBoundingClientRect().height;
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      const scrollPosition = window.scrollY;
      const footerHeight = 100;

      if (menuBottom <= windowHeight - buttonHeight - 20) {
        setIsButtonStatic(true);
      } else {
        setIsButtonStatic(false);
      }

      const distanceFromBottom = documentHeight - (scrollPosition + windowHeight);
      setIsBelowFooter(distanceFromBottom <= footerHeight);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const filteredItems = items
    .filter((item) => (activeCategory === "all" ? true : item.category === activeCategory))
    .filter((item) => item.name.toLowerCase().includes(searchQuery.toLowerCase()))
    .sort((a, b) => {
      if (sortOption === "a-z") return a.name.localeCompare(b.name);
      if (sortOption === "z-a") return b.name.localeCompare(a.name);
      if (sortOption === "low-high") return a.price - b.price;
      if (sortOption === "high-low") return b.price - a.price;
      return 0;
    });

  const totalPrice = selectedItems.reduce((sum, selectedItem) => sum + selectedItem.item.price * selectedItem.quantity, 0);
  const totalItems = selectedItems.reduce((sum, selectedItem) => sum + selectedItem.quantity, 0);

  if (loading) {
    return (
      <section className="min-h-screen bg-gray-50 dark:bg-gray-900 py-10 px-4 pb-20">
        <button onClick={() => navigate(-1)} className="md:hidden flex items-center text-gray-900 dark:text-white mb-4">
          <ArrowLeft className="w-6 h-6 mr-2" /> Back
        </button>
        <h2 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-8 text-center">
          Loading Menu...
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
          {[...Array(8)].map((_, i) => (
            <div key={i} className="animate-pulse bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden">
              <div className="h-48 bg-gray-300 dark:bg-gray-700" />
              <div className="p-4">
                <div className="h-4 bg-gray-300 dark:bg-gray-700 rounded w-3/4 mb-2" />
                <div className="h-4 bg-gray-300 dark:bg-gray-700 rounded w-1/2" />
              </div>
            </div>
          ))}
        </div>
      </section>
    );
  }

  return (
    <section className="min-h-screen bg-gray-50 dark:bg-gray-900 py-10 px-4 sm:px-6 lg:px-8 relative pb-20">
      <div className="max-w-7xl mx-auto">
        <button onClick={() => navigate(-1)} className="md:hidden flex items-center text-gray-900 dark:text-white mb-4">
          <ArrowLeft className="w-6 h-6 mr-2" /> Back
        </button>
        <h1 className="text-2xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4 text-center">
          Takeaway Menu
        </h1>
        <p className="text-base md:text-lg text-gray-600 dark:text-gray-300 mb-8 text-center">
          Select items for your takeaway order.
        </p>

        <div ref={menuContainerRef} className="flex flex-col space-y-4 sm:space-y-6 mb-6 sm:mb-8">
          <div className="flex justify-center">
            <input
              type="text"
              placeholder="Search items..."
              onChange={(e) => debouncedSearch(e.target.value)}
              className="w-full sm:w-1/2 px-4 py-2 text-sm sm:text-base rounded-lg border border-gray-300 dark:border-gray-700 focus:outline-none focus:ring-2 focus:ring-emerald-600"
            />
          </div>
          <div className="flex flex-col sm:flex-row sm:justify-center sm:space-x-4 space-y-4 sm:space-y-0">
            <div className="flex justify-center space-x-2 sm:space-x-4">
              <button
                onClick={() => setActiveCategory("all")}
                className={`px-3 py-2 text-sm sm:px-4 sm:text-base rounded-lg ${
                  activeCategory === "all"
                    ? "bg-emerald-600 text-white"
                    : "bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200"
                }`}
              >
                All
              </button>
              <button
                onClick={() => setActiveCategory("veg")}
                className={`px-3 py-2 text-sm sm:px-4 sm:text-base rounded-lg ${
                  activeCategory === "veg"
                    ? "bg-emerald-600 text-white"
                    : "bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200"
                }`}
              >
                Veg
              </button>
              <button
                onClick={() => setActiveCategory("non-veg")}
                className={`px-3 py-2 text-sm sm:px-4 sm:text-base rounded-lg ${
                  activeCategory === "non-veg"
                    ? "bg-emerald-600 text-white"
                    : "bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200"
                }`}
              >
                Non-Veg
              </button>
            </div>
            <div className="flex justify-center">
              <select
                value={sortOption}
                onChange={(e) => setSortOption(e.target.value)}
                className="w-full sm:w-auto px-2 py-2 text-sm sm:text-base rounded-lg border border-gray-300 dark:border-gray-700 focus:outline-none focus:ring-2 focus:ring-emerald-600"
              >
                <option value="">Sort By</option>
                <option value="a-z">A to Z</option>
                <option value="z-a">Z to A</option>
                <option value="low-high">Price: Low to High</option>
                <option value="high-low">Price: High to Low</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6">
            {filteredItems.map((item) => (
              <MenuCard
                key={item.id}
                item={item}
                selectedItems={selectedItems}
                setSelectedItems={setSelectedItems}
              />
            ))}
          </div>

          <div
            ref={buttonRef}
            className={`left-0 right-0 px-4 transition-all duration-300 ${
              isButtonStatic
                ? "static mt-8"
                : isBelowFooter
                ? "fixed bottom-24"
                : "fixed bottom-4"
            }`}
          >
            {/* This div is empty since the "Back to Takeaway Order" button is in the CartBar */}
          </div>
        </div>
      </div>

      <CartBar
        openModal={() => setIsCartModalOpen(true)}
        totalPrice={totalPrice}
        totalItems={totalItems}
        selectedItems={selectedItems}
        formData={formData}
      />

      {isCartModalOpen && (
        <CartModal
          closeModal={() => setIsCartModalOpen(false)}
          selectedItems={selectedItems}
          setSelectedItems={setSelectedItems}
          totalPrice={totalPrice}
          navigate={navigate}
          formData={formData}
        />
      )}
    </section>
  );
};

interface MenuCardProps {
  item: MenuItem;
  selectedItems: SelectedItem[];
  setSelectedItems: React.Dispatch<React.SetStateAction<SelectedItem[]>>;
}

const MenuCard: React.FC<MenuCardProps> = ({ item, selectedItems, setSelectedItems }) => {
  const [quantity, setQuantity] = useState(() => {
    const selected = selectedItems.find((si) => si.item.id === item.id);
    return selected ? selected.quantity : 0;
  });

  useEffect(() => {
    const selected = selectedItems.find((si) => si.item.id === item.id);
    setQuantity(selected ? selected.quantity : 0);
  }, [selectedItems, item.id]);

  const handleIncrement = () => {
    const existingItem = selectedItems.find((si) => si.item.id === item.id);
    let newQuantity: number;

    if (existingItem) {
      newQuantity = existingItem.quantity + 1;
      setSelectedItems((prevItems) =>
        prevItems.map((si) =>
          si.item.id === item.id ? { ...si, quantity: newQuantity } : si
        )
      );
    } else {
      newQuantity = 1;
      const newItem = {
        item: { ...item },
        quantity: newQuantity,
      };
      setSelectedItems((prevItems) => [...prevItems, newItem]);
    }

    setQuantity(newQuantity);
    const ordinalQuantity = getOrdinalSuffix(newQuantity);
    toast.success(`Added ${ordinalQuantity} ${item.name} to your takeaway order`, {
      id: `takeaway-toast-${item.id}`,
      duration: 3000,
      position: "top-right",
    });
  };

  const handleDecrement = () => {
    const existingItem = selectedItems.find((si) => si.item.id === item.id);
    if (existingItem && existingItem.quantity > 0) {
      const newQuantity = existingItem.quantity - 1;
      if (newQuantity === 0) {
        setSelectedItems((prevItems) => prevItems.filter((si) => si.item.id !== item.id));
        setQuantity(0);
        toast.dismiss(`takeaway-toast-${item.id}`);
      } else {
        setSelectedItems((prevItems) =>
          prevItems.map((si) =>
            si.item.id === item.id ? { ...si, quantity: newQuantity } : si
          )
        );
        setQuantity(newQuantity);
        const ordinalQuantity = getOrdinalSuffix(newQuantity);
        toast.success(`Updated to ${ordinalQuantity} ${item.name} in your takeaway order`, {
          id: `takeaway-toast-${item.id}`,
          duration: 3000,
          position: "top-right",
        });
      }
    }
  };

  const isAdded = quantity > 0;
  const buttonColor = !isAdded
    ? "bg-gray-300 dark:bg-gray-700 text-gray-900 dark:text-gray-300 hover:bg-gray-400 dark:hover:bg-gray-600"
    : item.category === "veg"
    ? "bg-emerald-600 hover:bg-emerald-700 text-white"
    : "bg-red-600 hover:bg-red-700 text-white";

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden transform transition-all hover:scale-105">
      <div className="flex flex-row sm:flex-col items-center sm:items-stretch">
        <img
          src={item.image}
          alt={item.name}
          className="w-16 h-16 sm:w-full sm:h-48 object-cover rounded-md sm:rounded-none flex-shrink-0"
        />
        <div className="flex-1 p-3 sm:p-4 flex flex-col justify-between sm:justify-start ml-3 sm:ml-0">
          <div className="flex items-center justify-between sm:flex-col sm:items-start sm:mb-2 gap-2">
            <div className="flex items-center gap-2">
              {item.category === "veg" ? (
                <img src="https://freesvg.org/img/1531813273.png" alt="Veg" className="w-4 h-4 sm:w-6 sm:h-6" />
              ) : (
                <img src="https://freesvg.org/img/1531813245.png" alt="Non-Veg" className="w-4 h-4 sm:w-6 sm:h-6" />
              )}
              <h3 className="text-sm sm:text-lg font-semibold text-gray-900 dark:text-white truncate">{item.name}</h3>
            </div>
            <span className="text-emerald-600 font-semibold text-xs sm:text-base">€{item.price.toFixed(2)}</span>
          </div>
          <div className="flex flex-col sm:flex-col sm:mt-0 mt-1">
            <p className="text-gray-600 dark:text-gray-400 text-xs sm:text-sm mb-2 sm:mb-4 line-clamp-2">{item.description}</p>
            <div className="flex items-center justify-between sm:flex-row sm:gap-2">
              <div className="flex items-center space-x-1 sm:space-x-2">
                <button
                  onClick={handleDecrement}
                  className="p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
                  disabled={quantity === 0}
                >
                  <Minus className="w-3 h-3 sm:w-4 sm:h-4" />
                </button>
                <span className="text-gray-900 dark:text-white w-6 sm:w-8 text-center text-xs sm:text-base">{quantity}</span>
                <button
                  onClick={handleIncrement}
                  className="p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  <Plus className="w-3 h-3 sm:w-4 sm:h-4" />
                </button>
              </div>
              <button
                onClick={handleIncrement}
                className={`w-full sm:w-auto px-2 py-1 sm:px-4 sm:py-2 rounded-lg text-xs sm:text-base ${buttonColor} transition-colors`}
              >
                Add to Takeaway
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const CartBar: React.FC<{
  openModal: () => void;
  totalPrice: number;
  totalItems: number;
  selectedItems: SelectedItem[];
  formData: any;
}> = ({ openModal, totalPrice, totalItems, selectedItems, formData }) => {
  const navigate = useNavigate();

  if (totalItems === 0) return null;

  return (
    <div
      id="cart-bar"
      className="sticky bottom-4 z-10 w-full px-4 sm:px-6 lg:px-8 bg-[#E6F4F1] dark:bg-[#D1E9E5] shadow-2xl border border-gray-300 dark:border-gray-600 rounded-lg"
    >
      <div className="py-2 sm:py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-4 max-w-7xl mx-auto">
        {/* Total Price and Items */}
        <div className="flex items-center space-x-2 sm:space-x-3">
          <span className="font-inter text-gray-900 dark:text-gray-800 text-sm sm:text-base font-semibold">
            €{totalPrice.toFixed(2)}
          </span>
          <span className="font-inter text-gray-600 dark:text-gray-500 text-sm sm:text-base">
            {totalItems} Item{totalItems !== 1 ? "s" : ""}
          </span>
        </div>

        {/* Selected Items List */}
        <div className="flex-1 overflow-x-auto">
          <div className="flex space-x-2 sm:space-x-3">
            {selectedItems.map((selectedItem) => (
              <div
                key={selectedItem.item.id}
                className="flex items-center space-x-1 bg-gray-100 dark:bg-gray-700 rounded-full px-2 py-0.5 sm:px-3 sm:py-1 whitespace-nowrap"
              >
                <span
                  className={`w-2 h-2 sm:w-3 sm:h-3 rounded-full ${
                    selectedItem.item.category === "veg" ? "bg-green-500" : "bg-red-500"
                  }`}
                ></span>
                <span className="text-gray-900 dark:text-white text-xs sm:text-sm">
                  {selectedItem.item.name}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Buttons */}
        <div className="flex space-x-2 sm:space-x-3">
          <button
            onClick={openModal}
            className="font-inter px-2 py-1 sm:px-3 sm:py-2 text-xs sm:text-sm rounded-lg bg-white dark:bg-gray-100 text-gray-900 dark:text-gray-800 border border-gray-300 dark:border-gray-400 hover:bg-gray-100 dark:hover:bg-gray-200 transition-colors"
          >
            View Items
          </button>
          <button
            onClick={() => navigate("/takeaway", { state: { formData, selectedItems } })}
            className="font-inter px-2 py-1 sm:px-3 sm:py-2 text-xs sm:text-sm rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 transition-colors flex items-center"
          >
            <ShoppingCart className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
            Back to Takeaway Order
          </button>
        </div>
      </div>
    </div>
  );
};

const CartModal: React.FC<{
  closeModal: () => void;
  selectedItems: SelectedItem[];
  setSelectedItems: React.Dispatch<React.SetStateAction<SelectedItem[]>>;
  totalPrice: number;
  navigate: (path: string, options?: { state?: any }) => void;
  formData: any;
}> = ({ closeModal, selectedItems, setSelectedItems, totalPrice, navigate, formData }) => {
  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      closeModal();
      navigate('/services/takeaway/menu', { state: { formData, selectedItems } });
    }
  };

  const handleIncrement = (itemId: string) => {
    const existingItem = selectedItems.find((si) => si.item.id === itemId);
    let newQuantity: number;

    if (existingItem) {
      newQuantity = existingItem.quantity + 1;
      setSelectedItems((prevItems) =>
        prevItems.map((selectedItem) =>
          selectedItem.item.id === itemId
            ? { ...selectedItem, quantity: newQuantity }
            : selectedItem
        )
      );
      const ordinalQuantity = getOrdinalSuffix(newQuantity);
      toast.success(`Added ${ordinalQuantity} ${existingItem.item.name} to your takeaway order`, {
        id: `takeaway-toast-${itemId}`,
        duration: 3000,
        position: "top-right",
      });
    }
  };

  const handleDecrement = (itemId: string) => {
    const existingItem = selectedItems.find((si) => si.item.id === itemId);
    if (existingItem && existingItem.quantity > 0) {
      const newQuantity = existingItem.quantity - 1;
      if (newQuantity === 0) {
        setSelectedItems((prevItems) =>
          prevItems.filter((selectedItem) => selectedItem.item.id !== itemId)
        );
        toast.dismiss(`takeaway-toast-${itemId}`);
      } else {
        setSelectedItems((prevItems) =>
          prevItems.map((selectedItem) =>
            selectedItem.item.id === itemId
              ? { ...selectedItem, quantity: newQuantity }
              : selectedItem
          )
        );
        const ordinalQuantity = getOrdinalSuffix(newQuantity);
        toast.success(`Updated to ${ordinalQuantity} ${existingItem.item.name} in your takeaway order`, {
          id: `takeaway-toast-${itemId}`,
          duration: 3000,
          position: "top-right",
        });
      }
    }
  };

  const handleRemove = (itemId: string) => {
    setSelectedItems((prevItems) => prevItems.filter((selectedItem) => selectedItem.item.id !== itemId));
    toast.dismiss(`takeaway-toast-${itemId}`);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
      onClick={handleOverlayClick}
    >
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg w-full max-w-md mx-4 p-6 max-h-[80vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h3 className="font-cormorant text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">Your Takeaway Order</h3>
          <button
            onClick={() => {
              closeModal();
              navigate('/takeaway', { state: { formData, selectedItems } });
            }}
            className="text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
          >
            <X className="w-6 h-6" />
          </button>
        </div>
        {selectedItems.length === 0 ? (
          <p className="font-inter text-gray-600 dark:text-gray-400 text-center">Your takeaway order is empty.</p>
        ) : (
          <>
            <div className="space-y-4">
              {selectedItems.map((selectedItem) => (
                <div key={selectedItem.item.id} className="flex items-center space-x-4">
                  <img
                    src={selectedItem.item.image}
                    alt={selectedItem.item.name}
                    className="w-12 h-12 sm:w-16 sm:h-16 object-cover rounded-md"
                  />
                  <div className="flex-1 flex items-center justify-between">
                    <div>
                      <h4 className="font-inter text-sm sm:text-base font-semibold text-gray-900 dark:text-white truncate">
                        {selectedItem.item.name}
                      </h4>
                      <p className="font-inter text-gray-600 dark:text-gray-400 text-xs sm:text-sm">
                        €{selectedItem.item.price.toFixed(2)} x {selectedItem.quantity} = €{(selectedItem.item.price * selectedItem.quantity).toFixed(2)}
                      </p>
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => handleDecrement(selectedItem.item.id)}
                        className="p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
                      >
                        <Minus className="w-3 h-3 sm:w-4 sm:h-4" />
                      </button>
                      <span className="font-inter text-gray-900 dark:text-white w-6 text-center text-xs sm:text-base">
                        {selectedItem.quantity}
                      </span>
                      <button
                        onClick={() => handleIncrement(selectedItem.item.id)}
                        className="p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
                      >
                        <Plus className="w-3 h-3 sm:w-4 sm:h-4" />
                      </button>
                      <button
                        onClick={() => handleRemove(selectedItem.item.id)}
                        className="font-inter text-red-600 hover:text-red-700 text-xs sm:text-sm"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-6 border-t border-gray-200 dark:border-gray-700 pt-4">
              <div className="flex justify-between items-center">
                <span className="font-inter text-gray-900 dark:text-white text-sm sm:text-base font-semibold">
                  Total:
                </span>
                <span className="font-inter text-emerald-600 dark:text-emerald-400 text-sm sm:text-base font-semibold">
                  €{totalPrice.toFixed(2)}
                </span>
              </div>
              <button
                onClick={() => {
                  closeModal();
                  navigate("/takeaway", { state: { formData, selectedItems } });
                }}
                className="w-full mt-4 font-inter px-4 py-2 text-sm sm:text-base rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 transition-colors"
              >
                Back to Takeaway Order
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default TakeawayMenu;